import type { Currency } from '@/types/donation';

/**
 * Format currency amount for display
 * @param amount - Amount in smallest unit (cents, kobo, pence)
 * @param currency - Currency code
 * @param locale - Locale for formatting (default: en-US)
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currency: Currency,
  locale: string = 'en-US'
): string {
  // Convert from smallest unit to major unit
  const majorUnit = amount / 100;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(majorUnit);
}

/**
 * Convert user input to smallest currency unit
 * @param amount - Amount in major unit (dollars, naira)
 * @returns Amount in smallest unit (cents, kobo)
 */
export function toSmallestUnit(amount: number): number {
  return Math.round(amount * 100);
}

/**
 * Convert smallest unit to major unit
 * @param amount - Amount in smallest unit
 * @returns Amount in major unit
 */
export function toMajorUnit(amount: number): number {
  return amount / 100;
}

/**
 * Get preset donation amounts for a currency
 * @param currency - Currency code
 * @returns Array of preset amounts in smallest unit
 */
export function getPresetAmounts(currency: Currency): number[] {
  const presets: Record<Currency, number[]> = {
    NGN: [500000, 1000000, 2500000, 5000000], // ₦5,000, ₦10,000, ₦25,000, ₦50,000
    USD: [1000, 2500, 5000, 10000], // $10, $25, $50, $100
    GHS: [10000, 25000, 50000, 100000], // GH₵100, GH₵250, GH₵500, GH₵1,000
    KES: [100000, 250000, 500000, 1000000], // KSh1,000, KSh2,500, KSh5,000, KSh10,000
    CAD: [1000, 2500, 5000, 10000], // CA$10, CA$25, CA$50, CA$100
    EUR: [1000, 2500, 5000, 10000], // €10, €25, €50, €100
    GBP: [1000, 2500, 5000, 10000], // £10, £25, £50, £100
  };

  return presets[currency] || presets.USD;
}

/**
 * Get currency symbol
 * @param currency - Currency code
 * @returns Currency symbol
 */
export function getCurrencySymbol(currency: Currency): string {
  const symbols: Record<Currency, string> = {
    NGN: '₦',
    USD: '$',
    GHS: 'GH₵',
    KES: 'KSh',
    CAD: 'CA$',
    EUR: '€',
    GBP: '£',
  };

  return symbols[currency] || '$';
}
